import { isNight } from '../state/rules';
import type { Interactions } from '../systems/Interactions';
import { BarRoom, BAR_SPOTS } from '../world/BarRoom';
import { SPOTS, type Vec2 } from '../world/layout';
import { hourOf } from '../state/rules';
import type { BullMount } from '../stages/BullStage';
import type { Games } from './Games';
import type { Interiors } from './Interiors';
import type { GameServices } from './services';

/** What happens when a song comes on, keyed by the jukebox's pick. */
const SONGS: Record<string, string> = {
  'Mullet Over': 'A man in the corner stands up and salutes. Nobody knows why.',
  'She Took the Satellite Dish':
    'Three women at the bar look at the same guy. He leaves. He also took a satellite dish once.',
  'Lot Rent Shuffle': "Lurlene turns it up herself. That's the highest honor the Rusty Spur gives.",
  'Widowmaker Hoedown': 'Somebody yells "FREE BIRD" at it anyway. Perfect harmony.',
  'Cooler Full of Natty': "The whole bar sings along. It's an instrumental. They sing it anyway.",
};

/**
 * The south strip: the Booze Barn, the Rusty Spur (which you can walk into) and
 * Cooter's Used Cars.
 */
export class Strip {
  readonly bar: BarRoom;

  constructor(
    private readonly s: GameServices,
    private readonly interiors: Interiors,
    private readonly games: Games,
  ) {
    this.bar = new BarRoom(s.world.dynamic, s.world.collision);
    interiors.addPublic(this.bar, { x: SPOTS.barDoor.x, z: SPOTS.barDoor.z - 0.6 });
  }

  private open(): boolean {
    const h = hourOf(this.s.store.minutes);
    return h >= 11 || h < 2;
  }

  register(interactions: Interactions): void {
    const { store, ui, rng } = this.s;
    const add = interactions.add.bind(interactions);
    const inBar =
      (p: Vec2): (() => Vec2) =>
      () =>
        this.bar.world(p);

    add({
      id: 'barDoor',
      at: () => SPOTS.barDoor,
      radius: 1.8,
      prompt: () => (this.open() ? 'Go into the Rusty Spur' : 'The Rusty Spur (closed till 11 AM)'),
      action: () => {
        if (!this.open()) {
          store.toast('Locked. A sign says "CLOSED. GO HOME. YOUR MAMA MISSES YOU."', 'info', ':beer:');
          return;
        }
        this.interiors.enterPublic(this.bar.slot);
        if (isNight(store.minutes) && !store.doneToday('bar_welcome')) {
          store.markToday('bar_welcome');
          store.toast(
            "The jukebox, the neon, the smell of a thousand spilled beers. You're home.",
            'good',
            ':cowboy:',
          );
        }
      },
      priority: 0.5,
    });
    add({
      id: 'barExit',
      at: () => this.bar.exit,
      radius: 1.2,
      prompt: () => 'Head outside',
      action: () => this.interiors.leave(),
      priority: 0.5,
      icon: ':door:',
    });
    add({
      id: 'barDarts',
      at: inBar(BAR_SPOTS.darts),
      radius: 1.4,
      prompt: () => 'Throw darts (bet Lurlene)',
      icon: ':target:',
      highlight: () => this.bar.dartboard,
      action: () =>
        ui.modal.show({
          title: 'Darts',
          art: ':target:',
          text: "Lurlene wipes her hands on a bar towel. 'Three rounds, three darts, high score takes the money. I'll warn you: I'm good. I'll warn you twice: I'm mean about it.'",
          choices: [5, 10, 20].map((bet) => ({
            label: `Play for $${bet}`,
            hint: `win +$${bet}`,
            requires: ({ store: st }) => st.canAfford(bet) || `Need $${bet}`,
            run: () => {
              this.games.play('darts', bet);
            },
          })),
          leave: 'Maybe later',
        }),
    });
    add({
      id: 'barBull',
      at: inBar(BAR_SPOTS.bullStep),
      radius: 1.6,
      prompt: () => 'Ride the mechanical bull ($5)',
      icon: ':bull:',
      highlight: () => this.bar.mechBull,
      action: () => this.games.play('mechbull', 5),
    });
    add({
      id: 'barPoker',
      at: inBar({ x: BAR_SPOTS.poker.x - 0.9, z: BAR_SPOTS.poker.z + 0.5 }),
      radius: 1.5,
      prompt: () => 'Play video poker',
      icon: ':cards:',
      highlight: () => this.bar.props.poker,
      action: () =>
        ui.modal.show({
          title: 'Video Poker',
          art: ':cards:',
          text: 'The machine glows. It is the only thing in this bar that has never judged you. It just wants your money.',
          choices: [1, 5, 10].map((bet) => ({
            label: `Bet $${bet}`,
            hint: 'Jacks or Better',
            requires: ({ store: st }) => st.canAfford(bet) || `Need $${bet}`,
            run: () => {
              this.games.play('poker', bet);
            },
          })),
          leave: 'Walk away',
        }),
    });
    add({
      id: 'barJukebox',
      at: inBar({ x: BAR_SPOTS.jukebox.x + 0.9, z: BAR_SPOTS.jukebox.z }),
      radius: 1.3,
      prompt: () => 'Play a song on the jukebox ($1)',
      icon: ':notes:',
      highlight: () => this.bar.props.jukebox,
      action: () => {
        if (!store.spend(1)) {
          store.toast('The jukebox wants a dollar. You have feelings, but not a dollar.', 'bad', ':notes:');
          return;
        }
        this.s.sfx('crowd');
        store.addClout(store.doneToday('jukebox') ? 0 : 2);
        store.markToday('jukebox');
        const song = this.s.audio.jukebox();
        store.toast(`You punch in "${song}." ${SONGS[song] ?? 'It slaps.'}`, 'meme', ':notes:');
      },
    });
    add({
      id: 'barPool',
      at: inBar({ x: BAR_SPOTS.pool.x, z: BAR_SPOTS.pool.z + 1.1 }),
      radius: 1.4,
      prompt: () => (store.doneToday('pool') ? null : 'Shoot a rack of pool'),
      icon: ':eight-ball:',
      highlight: () => this.bar.props.pool,
      action: () => {
        store.markToday('pool');
        const win = rng.chance(0.45 + Math.min(0.2, store.rating() / 600));
        store.apply({ minutes: 30, clout: win ? 3 : 0 });
        store.toast(
          win
            ? "You sink the 8 off two rails and blow on the cue like it's a smoking gun. Nobody saw. You tell everybody."
            : 'You scratch on the 8. A man named Hoss says "tough break" and means it in every possible way.',
          win ? 'good' : 'info',
          ':eight-ball:',
        );
      },
    });
  }

  update(dt: number): void {
    void dt;
  }

  /** The mechanical bull, for climbing on. */
  bullMount(): BullMount {
    const bar = this.bar;
    const at = bar.world(BAR_SPOTS.bull);
    return {
      kind: 'mechanical',
      bull: bar.mechBull,
      backY: 1.425,
      exit: bar.world(BAR_SPOTS.bullStep),
      orbit: { x: at.x - 0.6, z: at.z + 0.8, r: 5.2, y: 3.0 },
      setBusy: (busy) => (bar.bullDriven = busy),
      parent: this.s.world.dynamic,
    };
  }
}
